<template>
  <div
    v-if="state.status !== 'loaded' || schedule || canEdit"
    class="mt-4 flex min-h-8 items-center gap-3 text-sm"
    data-testid="runner-schedule"
  >
    <CalendarDaysIcon class="size-5 shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />

    <p v-if="state.status === 'loading'" class="text-gray-400 dark:text-gray-500">
      Loading schedule…
    </p>

    <template v-else-if="state.status === 'error'">
      <p class="flex-1 text-gray-500 dark:text-gray-400">The schedule could not be loaded.</p>
      <button
        type="button"
        class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
        @click="emit('retry')"
      >
        Retry
      </button>
    </template>

    <template v-else-if="schedule">
      <div class="min-w-0 flex-1">
        <p
          class="font-medium break-words"
          :class="
            schedule.enabled ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
          "
          data-testid="runner-schedule-summary"
        >
          {{ summary }}
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400" data-testid="runner-schedule-hint">
          {{ hint }}
        </p>
      </div>
      <template v-if="canEdit">
        <button
          type="button"
          role="switch"
          :aria-checked="schedule.enabled"
          :aria-label="schedule.enabled ? 'Disable schedule' : 'Enable schedule'"
          :title="schedule.enabled ? 'Disable schedule' : 'Enable schedule'"
          :disabled="toggling"
          class="relative inline-flex h-5 w-9 shrink-0 rounded-full p-0.5 outline-offset-2 outline-indigo-600 transition-colors duration-200 ease-in-out focus-visible:outline-2 disabled:cursor-wait disabled:opacity-50"
          :class="
            schedule.enabled ? 'bg-indigo-600 dark:bg-indigo-500' : 'bg-gray-200 dark:bg-white/10'
          "
          data-testid="runner-schedule-toggle"
          @click="toggle"
        >
          <span
            aria-hidden="true"
            class="size-4 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out"
            :class="schedule.enabled ? 'translate-x-4' : 'translate-x-0'"
          />
        </button>
        <button
          type="button"
          class="inline-flex shrink-0 items-center rounded-md p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:text-gray-400 dark:hover:bg-white/10 dark:hover:text-gray-200"
          aria-label="Edit schedule"
          title="Edit schedule"
          data-testid="runner-schedule-edit"
          @click="dialogOpen = true"
        >
          <PencilSquareIcon class="size-5" aria-hidden="true" />
        </button>
      </template>
    </template>

    <button
      v-else
      type="button"
      class="text-left font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
      data-testid="runner-schedule-add"
      @click="dialogOpen = true"
    >
      Add schedule
      <span class="font-normal text-gray-500 dark:text-gray-400"
        >· pause and resume automatically</span
      >
    </button>

    <RunnerScheduleDialog
      v-if="canEdit && state.status === 'loaded'"
      :open="dialogOpen"
      :runner-id="runnerId"
      :runner-name="runnerName"
      :schedule="schedule"
      @close="dialogOpen = false"
      @saved="onSaved"
      @deleted="onDeleted"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { CalendarDaysIcon, PencilSquareIcon } from '@heroicons/vue/20/solid';
import RunnerScheduleDialog from '@/components/RunnerScheduleDialog.vue';
import { type RunnerSchedule, setRunnerScheduleEnabled } from '@/api/schedules';
import { runnerProblem } from '@/api/runners';
import type { ScheduleState } from '@/lib/runner-schedules';
import { formatScheduleEvent, nextScheduleEvent, scheduleSummary } from '@/lib/schedule';
import { useToastStore } from '@/stores/toast';

const props = defineProps<{
  runnerId: string;
  runnerName: string;
  state: ScheduleState;
  canEdit: boolean;
  now: Date;
}>();

const emit = defineEmits<{
  (e: 'change', schedule: RunnerSchedule | null): void;
  (e: 'retry'): void;
}>();

const toast = useToastStore();
const dialogOpen = ref(false);
const toggling = ref(false);

const schedule = computed(() => (props.state.status === 'loaded' ? props.state.schedule : null));
const summary = computed(() => (schedule.value ? scheduleSummary(schedule.value) : ''));

const hint = computed(() => {
  if (!schedule.value) return '';
  if (!schedule.value.enabled) return 'Disabled';
  const event = nextScheduleEvent(schedule.value, props.now);
  return event ? `Next: ${formatScheduleEvent(event)}` : '';
});

async function toggle() {
  if (!schedule.value || toggling.value) return;
  toggling.value = true;
  const enable = !schedule.value.enabled;
  try {
    emit('change', await setRunnerScheduleEnabled(props.runnerId, enable));
  } catch (e) {
    const fallback = `The schedule could not be ${enable ? 'enabled' : 'disabled'}.`;
    toast.error(runnerProblem(e, fallback).message);
  } finally {
    toggling.value = false;
  }
}

function onSaved(saved: RunnerSchedule) {
  dialogOpen.value = false;
  emit('change', saved);
  toast.success(`Schedule of ${props.runnerName} saved.`);
}

function onDeleted() {
  dialogOpen.value = false;
  emit('change', null);
  toast.success(`Schedule of ${props.runnerName} deleted.`);
}
</script>
